import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

// ─── Walkthrough Steps ────────────────────────────────────────────────────────
const WALKTHROUGH_STEPS = [
  {
    id: 'booking',
    step: 1,
    icon: 'CalendarCheck',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    title: 'Réservation',
    subtitle: 'Initier la transaction',
    description:
      'Commencez par réserver une consultation avec un notaire. Sélectionnez votre bien, définissez le prix de vente et choisissez un notaire disponible dans votre commune.',
    tooltip:
      'Le notaire vérifie la légalité de la transaction et protège les deux parties.',
    helpLink: '/consultation-booking-hub',
    helpLabel: 'Réserver une consultation',
    badge: 'Étape 1',
    badgeColor: 'bg-blue-100 text-blue-700',
    forBuyer: true,
    forSeller: true,
    tips: [
      'Préparez vos documents d\'identité',
      'Ayez le titre de propriété à portée',
      'Choisissez un notaire certifié Kapital States',
    ],
  },
  {
    id: 'payment',
    step: 2,
    icon: 'CreditCard',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    title: 'Paiement',
    subtitle: 'Sécuriser les fonds',
    description:
      'L\'acheteur effectue le paiement via Paystack ou virement bancaire. Les fonds sont immédiatement capturés et mis en attente de validation par le notaire.',
    tooltip:
      'Votre paiement est sécurisé par chiffrement SSL et validé par Paystack avant tout transfert.',
    helpLink: '/mon-espace',
    helpLabel: 'Voir mes transactions',
    badge: 'Étape 2',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    forBuyer: true,
    forSeller: false,
    tips: [
      'Utilisez un réseau sécurisé pour payer',
      'Conservez votre reçu de paiement',
      'Vérifiez le montant avant de confirmer',
    ],
  },
  {
    id: 'escrow',
    step: 3,
    icon: 'Shield',
    iconBg: 'bg-brand-earth/10',
    iconColor: 'text-brand-earth',
    title: 'Séquestre',
    subtitle: 'Fonds bloqués en sécurité',
    description:
      'Les fonds sont placés en séquestre chez le notaire. Ni l\'acheteur ni le vendeur ne peut y accéder tant que toutes les conditions contractuelles ne sont pas remplies.',
    tooltip:
      'Le séquestre garantit que le vendeur reçoit son paiement uniquement après transfert légal du titre.',
    helpLink: '/demarches-administratives',
    helpLabel: 'Guide des démarches',
    badge: 'Étape 3',
    badgeColor: 'bg-brand-earth/15 text-brand-earth',
    forBuyer: true,
    forSeller: true,
    tips: [
      'Suivez l\'état du séquestre dans Mon Espace',
      'Le notaire vous notifie à chaque étape',
      'Durée typique : 30 à 90 jours',
    ],
  },
  {
    id: 'dispute',
    step: 4,
    icon: 'AlertTriangle',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    title: 'Litige (si applicable)',
    subtitle: 'Résolution des conflits',
    description:
      'En cas de désaccord, l\'une ou l\'autre partie peut ouvrir un litige. Le notaire examine les preuves soumises et rend une décision dans un délai de 15 jours ouvrables.',
    tooltip:
      'Les litiges sont traités par un notaire certifié avec accès à toutes les preuves documentaires.',
    helpLink: '/legal-services-center',
    helpLabel: 'Centre de services légaux',
    badge: 'Optionnel',
    badgeColor: 'bg-amber-100 text-amber-700',
    forBuyer: true,
    forSeller: true,
    tips: [
      'Soumettez vos preuves dans les 48h',
      'Utilisez la messagerie sécurisée',
      'Un médiateur peut être désigné',
    ],
  },
  {
    id: 'release',
    step: 5,
    icon: 'CheckCircle',
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    title: 'Libération',
    subtitle: 'Transaction finalisée',
    description:
      'Une fois toutes les conditions remplies et le titre transféré, le notaire libère les fonds vers le vendeur. L\'acheteur reçoit confirmation du transfert de propriété.',
    tooltip:
      'La libération déclenche automatiquement l\'envoi des documents finaux et la mise à jour du registre foncier.',
    helpLink: '/mon-espace',
    helpLabel: 'Voir Mon Espace',
    badge: 'Étape 5',
    badgeColor: 'bg-green-100 text-green-700',
    forBuyer: true,
    forSeller: true,
    tips: [
      'Téléchargez votre acte de propriété',
      'Vérifiez le virement reçu',
      'Notez la référence de transaction',
    ],
  },
];

// ─── Tooltip Component ────────────────────────────────────────────────────────
const Tooltip = ({ text, children }) => {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        className="cursor-help"
      >
        {children}
      </span>
      {visible && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-56 bg-gray-900 text-white text-xs rounded-xl px-3 py-2 shadow-lg pointer-events-none leading-relaxed">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
};

// ─── Step Card ────────────────────────────────────────────────────────────────
const StepCard = ({ step, isActive, isCompleted, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 ${
      isActive
        ? 'border-brand-earth bg-brand-earth/5 shadow-ios-medium'
        : isCompleted
        ? 'border-green-200 bg-green-50/50' :'border-border bg-white dark:bg-card hover:border-brand-earth/40 hover:shadow-ios-light'
    }`}
  >
    <div className="flex items-center gap-3">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCompleted ? 'bg-green-100' : step?.iconBg
        }`}
      >
        <Icon
          name={isCompleted ? 'Check' : step?.icon}
          size={18}
          className={isCompleted ? 'text-green-600' : step?.iconColor}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-text-primary">{step?.title}</p>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${step?.badgeColor}`}>
            {step?.badge}
          </span>
        </div>
        <p className="text-xs text-text-secondary mt-0.5 truncate">{step?.subtitle}</p>
      </div>
      <Icon
        name={isActive ? 'ChevronDown' : 'ChevronRight'}
        size={14}
        className="text-text-secondary flex-shrink-0"
      />
    </div>
  </button>
);

// ─── Main Walkthrough Component ───────────────────────────────────────────────
const EscrowWalkthrough = ({ onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [role, setRole] = useState('both'); // 'buyer' | 'seller' | 'both'
  const [dismissed, setDismissed] = useState(false);
  const contentRef = useRef(null);

  const filteredSteps = WALKTHROUGH_STEPS?.filter((s) => {
    if (role === 'buyer') return s?.forBuyer;
    if (role === 'seller') return s?.forSeller;
    return true;
  });

  const currentStep = filteredSteps?.[activeStep];

  const handleNext = () => {
    setCompletedSteps((prev) => new Set([...prev, activeStep]));
    if (activeStep < filteredSteps?.length - 1) {
      setActiveStep((p) => p + 1);
    }
  };

  const handlePrev = () => {
    if (activeStep > 0) setActiveStep((p) => p - 1);
  };

  const handleStepClick = (idx) => {
    setActiveStep(idx);
  };

  const handleClose = () => {
    setDismissed(true);
    onClose?.();
  };

  if (dismissed) return null;

  return (
    <div className="bg-white dark:bg-card rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/40 bg-gradient-to-r from-brand-earth/5 to-brand-navy/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-brand-earth/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Map" size={18} className="text-brand-earth" />
          </div>
          <div>
            <p className="text-ios-callout font-body font-semibold text-text-primary">
              Guide du Workflow Séquestre
            </p>
            <p className="text-ios-caption1 text-text-secondary mt-0.5">
              Comprendre chaque étape de votre transaction
            </p>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="w-7 h-7 rounded-full bg-surface flex items-center justify-center hover:bg-border/50 transition-colors"
        >
          <Icon name="X" size={14} className="text-text-secondary" />
        </button>
      </div>

      {/* Role selector */}
      <div className="px-5 py-3 border-b border-border/30 bg-surface/40">
        <p className="text-xs font-semibold text-text-secondary mb-2">Je suis :</p>
        <div className="flex gap-2">
          {[
            { value: 'buyer', label: 'Acheteur', icon: 'ShoppingBag' },
            { value: 'seller', label: 'Vendeur', icon: 'Home' },
            { value: 'both', label: 'Les deux', icon: 'Users' },
          ]?.map((r) => (
            <button
              key={r?.value}
              onClick={() => { setRole(r?.value); setActiveStep(0); setCompletedSteps(new Set()); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                role === r?.value
                  ? 'bg-brand-earth text-white border-brand-earth' :'bg-white border-border text-text-secondary hover:border-brand-earth/40'
              }`}
            >
              <Icon name={r?.icon} size={12} />
              {r?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Step list sidebar */}
        <div className="lg:w-64 p-4 border-b lg:border-b-0 lg:border-r border-border/30 space-y-2 bg-surface/20">
          {filteredSteps?.map((step, idx) => (
            <StepCard
              key={step?.id}
              step={step}
              isActive={idx === activeStep}
              isCompleted={completedSteps?.has(idx)}
              onClick={() => handleStepClick(idx)}
            />
          ))}
        </div>

        {/* Step detail */}
        <div className="flex-1 p-5" ref={contentRef}>
          {currentStep && (
            <div className="space-y-4">
              {/* Step header */}
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-2xl ${currentStep?.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={currentStep?.icon} size={26} className={currentStep?.iconColor} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-heading font-bold text-text-primary">{currentStep?.title}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${currentStep?.badgeColor}`}>
                      {currentStep?.badge}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mt-0.5">{currentStep?.subtitle}</p>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-text-primary leading-relaxed">{currentStep?.description}</p>

              {/* Tooltip info box */}
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <Tooltip text={currentStep?.tooltip}>
                  <Icon name="Info" size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                </Tooltip>
                <p className="text-xs text-blue-700 leading-relaxed">{currentStep?.tooltip}</p>
              </div>

              {/* Tips */}
              <div className="bg-surface/60 rounded-xl p-4">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                  Conseils pratiques
                </p>
                <ul className="space-y-1.5">
                  {currentStep?.tips?.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-text-primary">
                      <Icon name="CheckCircle" size={13} className="text-brand-earth mt-0.5 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Help link */}
              <a
                href={currentStep?.helpLink}
                className="inline-flex items-center gap-2 text-xs font-semibold text-brand-earth hover:text-brand-earth/80 transition-colors"
              >
                <Icon name="ExternalLink" size={13} />
                {currentStep?.helpLabel}
              </a>

              {/* Progress + navigation */}
              <div className="flex items-center justify-between pt-2 border-t border-border/30">
                <div className="flex items-center gap-1">
                  {filteredSteps?.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        idx === activeStep
                          ? 'w-6 bg-brand-earth'
                          : completedSteps?.has(idx)
                          ? 'w-3 bg-green-400' :'w-3 bg-border'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrev}
                    disabled={activeStep === 0}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold border border-border text-text-secondary hover:border-brand-earth/40 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <Icon name="ChevronLeft" size={13} />
                    Précédent
                  </button>
                  {activeStep < filteredSteps?.length - 1 ? (
                    <button
                      onClick={handleNext}
                      className="flex items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-semibold bg-brand-earth text-white hover:bg-brand-earth/90 transition-all"
                    >
                      Suivant
                      <Icon name="ChevronRight" size={13} />
                    </button>
                  ) : (
                    <button
                      onClick={handleClose}
                      className="flex items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition-all"
                    >
                      <Icon name="Check" size={13} />
                      Terminer le guide
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscrowWalkthrough;
